import React from 'react';
import styled from 'styled-components';
import { Tile, TileWrapper } from '../../atoms/Tile';
import { getCompanyColor } from '~/helpers/getColors';
import { CopyStandard } from '../../atoms/Copytext';
import { CopyBigSkin } from '../../atoms/Copytext/CopytextSkins';
import CompanyLogo from '../../atoms/CompanyLogo';
import { mq } from '~/styles/grid';
import Bookmark from '../Bookmark/Bookmark';
import useIsMobile from '~/hooks/useIsMobile';

const StyledLink = styled.a`
  padding: 25px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  text-decoration: none;
  height: 100%;

  .richText {
    margin: 0px !important;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-bottom: 20px;
  span {
    height: 52px;
    width: 131px;
  }
`;

/**
 * @param {Object} props
 * @param {CompanyLogoType} props.logo
 */
const CompanyIcon = styled(CompanyLogo)`
  color: ${(props) => getCompanyColor(props.logo)} !important;
  width: auto;
  height: 100%;
`;

const Location = styled(CopyStandard)`
  display: block;
  padding-bottom: 15px;
  color: ${(props) => props.theme.color.gray600} !important;
`;

const BookmarkWrapper = styled.div`
  width: fit-content;
  height: fit-content;
  position: absolute;
  top: 40px;
  right: 20px;
`;

/**
 * @param {Object} props
 * @param {Theme} props.theme
 */
const JobTitle = styled.div`
  ${CopyBigSkin};
  text-decoration: none;
  color: ${(props) => props.theme.color.primary800} !important;
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: auto;

  &:hover {
    color: ${(props) => props.theme.color.cta};
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (${mq.xs}) {
    height: 100%;
  }

  ${TileWrapper} {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`;

/**
 * Job teaser component
 * @param {JobTeaserProps} props
 * @returns {React.ReactElement}
 */
const JobTeaser = ({
  title,
  url,
  businessSegment,
  location,
  className,
  bookmarkId,
}) => {
  const isMobileSmDown = useIsMobile({ useMq: 'smDown' });
  const isMobileMdDown = useIsMobile({ useMq: 'mdDown' });

  /**
   * Truncates the title based on current breakpoint
   * @param {string} str - The string to truncate
   * @returns {string} - The truncated string
   */
  const truncateTitle = (str) => {
    if (isMobileSmDown) {
      const charsAmount = 77;
      return str.length > charsAmount
        ? `${str.substr(0, charsAmount)}...`
        : str;
    }
    if (isMobileMdDown) {
      const charsAmount = 250;
      return str.length > charsAmount
        ? `${str.substr(0, charsAmount)}...`
        : str;
    }
    const charsAmount = 170;
    return str.length > charsAmount ? `${str.substr(0, charsAmount)}...` : str;
  };

  return (
    <Wrapper className={className} id="job-teaser-wrapper">
      <Tile isJobTeaser isClickable id="job-teaser-tile">
        <StyledLink
          href={url}
          title={title}
          target="_blank"
          id="job-teaser-styled-link"
        >
          <HeaderContainer id="job-teaser-header-container">
            {businessSegment && <CompanyIcon logo={businessSegment} hovered />}
          </HeaderContainer>
          {location && <Location>{location}</Location>}
          <JobTitle id="job-teaser-job-title">{truncateTitle(title)}</JobTitle>
          {bookmarkId && (
            <BookmarkWrapper
              onClick={(e) => e.preventDefault()}
              id="job-teaser-bookmark-wrapper"
            >
              <Bookmark
                id={bookmarkId}
                city={location}
                company={businessSegment}
                jobId=""
                jobTitle={title}
                context="JobTeaser"
              />
            </BookmarkWrapper>
          )}
        </StyledLink>
      </Tile>
    </Wrapper>
  );
};

export default JobTeaser;
